import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation, AfterViewInit } from '@angular/core';

import { fuseAnimations } from '@fuse/animations';
import { Subject, Observable, BehaviorSubject, merge } from 'rxjs';
import { DataSource } from '@angular/cdk/table';
import { takeUntil, tap } from 'rxjs/operators';
import { MatPaginator, MatSort, MatDialog, MatDialogRef, MatSnackBar } from '@angular/material';
import { NotificationsService } from '@fuse/services/notifications/notifications.service';
import { TranslateService } from '@ngx-translate/core';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import * as _ from 'lodash';

@Component({
    selector: 'app-notification-list',
    templateUrl: './notification-list.component.html',
    styleUrls: ['./notification-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations
})
export class NotificationListComponent implements OnInit, AfterViewInit, OnDestroy {
    private _unsubscribeAll: Subject<any>;

    dataSource: FilesDataSource | null;
    displayedColumns = ['notification', 'created_at', 'read_at'];
    notifications: any;
    totalPages = 2;
    totalElements = 0;
    sortField = 'created_at';
    sordOrder = 'desc';
    pageSize = 10;
    lang = 'sr';
    categories = [3, 6, 8, 16, 17, 18];

    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort: MatSort;

    constructor(
        private _notificationService: NotificationsService,
        public _matDialog: MatDialog,
        private _matSnackBar: MatSnackBar,
        private _translate: TranslateService,
        private _translationLoader: FuseTranslationLoaderService
    ) {
        this._unsubscribeAll = new Subject();
        this._notificationService.currentData = [];
    }

    private getTranslations(): void {
        this._translationLoader.getLastLang()
            .then(response => {
                this.lang = response['code'];
                this._translationLoader.getMultipleTranslationCategories(this.lang, this.categories);
            })
            .catch(err => Promise.reject(err))
            .then(() => {
            }, err => {
            });
    }

    private compareNotifs(arr1, arr2): boolean {
        let count = 0;
        arr1.forEach(el1 => {
            arr2.forEach(el2 => {
                if (el1.id === el2.id && el1.read_at === el2.read_at) {
                    count++;
                }
            });
        });

        return count === arr1.length && this._notificationService.currentData.length !== 0;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    loadNotificationsPage(comment: string): void {
        console.log('CALL: ' + comment);
        this.dataSource.loadNotifications(this.paginator.pageIndex + 1, this.paginator.pageSize, this.sort.active || 'created_at', this.sort.direction || 'desc', '');
    }

    markAsRead(notification: any): void {
        if (!notification.read_at) {
            this._notificationService.markAsRead(notification.id);
            // this.getTranslations();
            // this.loadNotificationsPage('markAsRead');
        }

    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnInit(): void {
        this.dataSource = new FilesDataSource(this._notificationService);
        this.dataSource.loadNotifications(1, this.pageSize, this.sortField, this.sordOrder, '');

        this._notificationService.allNotificationsChanged
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(response => {

                // refresh table only if there is a difference
                if (!this.compareNotifs(this._notificationService.currentData, response.data)) {

                    this.totalPages = Math.floor(response.total / this.paginator.pageSize) + 1;
                    this.totalElements = response.total;
                    this.dataSource.setData(response.data);

                    this._notificationService.currentData = response.data;
                }
            });

        this._notificationService.newNotifications
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(response => {
                this.loadNotificationsPage('newNotifications');
            });

        this._translationLoader.onLangChanged
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(value => {
                this.getTranslations();
            });
        // this.getTranslations();
    }

    ngAfterViewInit(): void {
        // reset the paginator after sorting
        this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);

        // on sort or paginate events, load a new page
        merge(this.sort.sortChange, this.paginator.page)
            .pipe(
                tap(() => this.loadNotificationsPage('ngAfterInit'))
            )
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe();
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
}

// Custom Angular CDK Data Source
export class FilesDataSource extends DataSource<any>
{
    private notificationsSubject = new BehaviorSubject<any[]>([]);
    private loadingSubject = new BehaviorSubject<boolean>(false);

    public loading$ = this.loadingSubject.asObservable();

    /**
     * Constructor
     *
     * @param {NotificationsService} _notificationService
     */
    constructor(
        private _notificationService: NotificationsService
    ) {
        super();
    }

    connect(): Observable<any[]> {
        return this.notificationsSubject.asObservable();
    }

    disconnect(): void {
        this.notificationsSubject.complete();
        this.loadingSubject.complete();
    }

    setData(data): void {
        this.notificationsSubject.next(data);
        this.loadingSubject.next(false);
    }

    compareNotifs(arr1, arr2): boolean {
        let count = 0;
        arr1.forEach(el1 => {
            arr2.forEach(el2 => {
                if (el1.id === el2.id && el1.read_at === el2.read_at) {
                    count++;
                }
            });

        });

        return count === arr1.length && this._notificationService.currentData.length !== 0;
    }

    loadNotifications(page: number = 1, limit: number = 20, orderBy: string = 'created_at', order: string = 'desc', search: string = ''): void {
        this.loadingSubject.next(true);
        this._notificationService.listAllNotifications(page, limit, orderBy, order, search).then(response => {
            const notifications = response.data;
            notifications.forEach((notification) => {
                let message = '';
                let message2 = '';
                let message3 = '';
                let message4 = '';
                if ((notification.data.message.match(/%/g) || []).length < 2) {
                    message = notification.data.message;
                }
                else {
                    if ((notification.data.message.match(/%/g) || []).length >= 4) {
                        message = notification.data.message.split('%').slice(0, 1)[0];
                        message2 = notification.data.message.split('%').slice(1, 2)[0];
                        message3 = notification.data.message.split('%').slice(2, 3)[0];
                        message4 = notification.data.message.split('%').slice(3, 4)[0];
                    }
                    else {
                        message = notification.data.message.split('%').slice(0, 1)[0];
                        message2 = notification.data.message.split('%').slice(1, 2)[0];

                    }

                }
                notification.data.message = message;
                notification.data.message2 = message2;
                notification.data.message3 = message3;
                notification.data.message4 = message4;
                // use setTranslation() with the third argument set to true
                // to append translations instead of replacing them
            });

            // refresh table only if there is a difference
            if (!this.compareNotifs(this._notificationService.currentData, notifications)) {
                this.notificationsSubject.next(notifications);
                this.loadingSubject.next(false);
                this._notificationService.currentData = notifications;
            }
        });
    }
}
